import './videoish_player.less';

import $ from 'jquery';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import autobind from 'class-autobind';
import some from 'lodash/some';
import map from 'lodash/map';
import noop from 'lodash/noop';
import classNames from 'classnames';

import userAgentDetect from 'zoomforth_zfutils/useragent_detect.js';
import { getPreviewCollaborationSettings } from 'zoomforth_codesbane_viewer/reducers/index.js';
import videojs from 'zoomforth_zfutils/video.js';

/*************************************************************************************************/
/*** PROPS ***************************************************************************************/
/*************************************************************************************************/

const PROP_TYPES = {
  autoplay: PropTypes.bool,
  posterImageUrl: PropTypes.string.isRequired,
  sources: PropTypes.arrayOf(PropTypes.shape({
    type: PropTypes.string.isRequired, // e.g., video/foo
    label: PropTypes.string,
    src: PropTypes.string.isRequired // a URL
  })).isRequired,
  getVideoPlayer: PropTypes.func.isRequired,
  subtitleTrack: PropTypes.any,
  onEnded: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
  onPause: PropTypes.func.isRequired,
  onPlay: PropTypes.func.isRequired,
  onPlayerSetUp: PropTypes.func.isRequired,
  onSeeked: PropTypes.func.isRequired,
  onStart: PropTypes.func.isRequired,
  onTimeUpdate: PropTypes.func.isRequired
};

const PROP_DEFAULTS = {
  autoplay: false,
  onEnded: noop,
  onError: noop,
  onPause: noop,
  onPlay: noop,
  onPlayerSetUp: noop,
  onSeeked: noop,
  onStart: noop,
  onTimeUpdate: noop,
  getVideoPlayer: noop
};

const mapStateToProps = (reduxState) => {
  const previewCollaborationSettings = getPreviewCollaborationSettings(reduxState);
  const isPreviewCollaborationEnabled = !!previewCollaborationSettings.isEnabled;

  const iframeWrapperProps = {};
  if(isPreviewCollaborationEnabled){
    iframeWrapperProps['data-velt-iframe-container'] = true;
  }
  
  return {
    iframeWrapperProps
  };
};

const mapDispatchToProps = () => { return {}; };

/*************************************************************************************************/
/*** LEGACY **************************************************************************************/
/*************************************************************************************************/

export const makeStandardMpegSource = (src, label)=>
  (
    { type: 'video/mp4', label, src}
  );

export const makeYoutubeSource = (youtubeId)=>
  (
    { 'type': 'video/youtube', 'src': `https://www.youtube.com/watch?v=${youtubeId}`}
  );

const getPlaybackProgressEventIntervalSecs = (durationSecs)=> {
  if (durationSecs > 120) {
    return 30;

  } else if (durationSecs > 60) {
    return 20;

  } else { // durationSecs < 60
    return 15;
  }
};


/**

  Play video or youtube sources. So named because we refer to our 
  "video or youtube" content as "videoish" content.

  Intercept the event stream from the VideoishPlayer and curate
  the set of events to be more reasonable for analytics

  - Ignore the final `pause` and `timeupdate` events--just emit `ended`
  - Only emit `timeupdate` events at a set interval rather
    than the arbitrary (and high) Hz at which the user agent emits

*/
class VideoishPlayer extends Component {

  constructor(props) {
    super(props);
    this._isFirstPlay = true;
    this._previousTime = 0;
    autobind(this);
  }

  getRelevantEventData() {
    return {
      duration: this._player.duration(),
      current_time: this._player.currentTime()
    };
  }

  getUseNativeControlsForTouch() {
    /*
      for iphones and other real mobile phones we want to allow the device to use its native controls
      because we are sure they are html5 (not flash) and because the version of video.js
      we are using doesn't handle them well enough.
    */
    return userAgentDetect.isMobile.isAnyRealDevice();
  }

  getPlugins() {
    return {
      // The resolution switcher is smart enough to disable itself for single sources
      // and smart enough to fetch and display options for all available youtube sources
      // See: https://github.com/kmoskwiak/videojs-resolution-switcher/blob/85f1e51c8babefca2d670dbd0b21a6307dbb4d21/lib/videojs-resolution-switcher.js#L350
      videoJsResolutionSwitcher: { default: 'high' }
    };
  }

  /*
    Return true if there is one youtube type source in the list of sources
  */
  getIsYoutubeSource() {
    return some(this.props.sources, ['type', 'video/youtube']);
  }

  getTechOrder() {
    return this.getIsYoutubeSource() ?
        // If any sources are youtube, force youtube
        ['youtube']
      : 
        ['html5']; // We do not support flash    
  }

  shouldComponentUpdate() {
    // Its impossible for react to keep the dom in sync because 
    // videojs does destructive modifications. As such, we just 
    // forbid updates entirely and maintain the same player object.
    // It is therefore the parent's job to set a key on this component
    // or otherwise ensure that props do not change.
    return false;
  }

  componentDidMount() {
    this.setUpPlayer(this.videoElem);
  }

  componentWillUnmount() {
    if (this.videoElem) {
      this.tearDownPlayer(this.videoElem);
    }
  }

  tearDownPlayer(videoElem) {
    videojs(videoElem).dispose();
  }

  setUpPlayer(videoElem) {
    const _this = this;
    videojs(videoElem, {}, function() {
      // In current context `this` is the player obj
      _this.handlePlayerSetUp(this);
    });  
  }

  handlePlayerSetUp(player) {
    const {
      onEnded,
      onError,
      onPause,
      onPlay,
      onSeeked,
      onTimeUpdate
    } = this.props;

    // Almost completely the same as HTML5 media events
    // https://developer.mozilla.org/en-US/docs/Web/Guide/Events/Media_events
    player.on('ended', this.handleEnded); // Emitted when video ends
    player.on('error', this.handleError);
    player.on('pause', this.handlePause); // Emitted upon user pause
    player.on('play', this.handlePlay); // Emitted on first play, or on un-pause; not emitted after seeking or un-stalling
    // TODO: Maybe someday track `stalling` and `playing`, but since this is likely to only 
    // confuse and alarm customers, we shall not add that complexity now
    player.on('seeked', this.handleSeeked); //  Emitted when seeking is completed by user 
    player.on('timeupdate', this.handleTimeUpdate); // Emitted at some useragent-dependent-hz while playing

    if (this.getUseNativeControlsForTouch()) {
      player.on('click', (e)=> {
        e.stopPropagation();
        e.preventDefault();
        if (player.paused()) {
          player.play();
        } else {
          player.pause();
        }
      });
    }

    this._player = player;

    if (this.getIsYoutubeSource()) {
      $(this._player.el_).find('iframe').attr('tabindex', '-1');
    }
  }

  handleEnded() {
    this.props.onEnded(this.getRelevantEventData());
  }

  handleError() {
    this.props.onError({ 
      ...this.getRelevantEventData(),
      error: this._player.error()
    });
  }

  handlePause() {
    if (this._player.currentTime() == this._player.duration()) {
      // Ignore the final "pause" event in favor of the `ended`
      return;
    }
    this.props.onPause(this.getRelevantEventData());
  }

  /**

   Calls `onStart` on the first play of a video and `onPlay` on all
   following plays

  */
  handlePlay() {
    const eventData = this.getRelevantEventData();

    if (this._isFirstPlay) {
      this.props.onStart(eventData);
      this._isFirstPlay = false;

    } else {
      this.props.onPlay(eventData);

    }
  }

  handleSeeked() {
    this.props.onSeeked(this.getRelevantEventData());
  }

  /**

   Throttles calls to `onTimeUpdate` based on getPlaybackProgressEventIntervalSecs

  */
  handleTimeUpdate() { 
    const duration = this._player.duration();
    // Apprx # secs between video events
    const desiredIntervalSecs = getPlaybackProgressEventIntervalSecs(duration);
    const currentTime = this._player.currentTime();
    // Split the intervals up equally over the duration
    const intervalSecs = duration / parseInt(duration / desiredIntervalSecs);


    if (
      // Ignore the timeupdate event on the first go-round
      this._previousTime &&
      // Ignore the final timeupdate
      duration !== currentTime &&
      // When the modulo of the previous time is a higher than the 
      // current time that means we've 
      (this._previousTime % intervalSecs) > (currentTime % intervalSecs)
    ) {
      this.props.onTimeUpdate(this.getRelevantEventData());
    }
    this._previousTime = currentTime;
  }

  render() {
    const { 
      id,
      autoplay, 
      className,
      getVideoPlayer,
      posterImageUrl, 
      sources,
      subtitleTrack,
      canDownloadVideo,
      iframeWrapperProps
    } = this.props;

    return (
      <video
        id={id}
        ref={ref => {
          this.videoElem = ref;
          getVideoPlayer(this);
        }}
        autoPlay={autoplay}
        controls
        className={classNames(className, 'video-js vjs-default-skin vjs-big-play-centered')}
        height="100%"
        width="100%"
        onContextMenu={(e)=> {
          // preventing downloads of videos online is impossible by the nature
          // of the internet. there are other ways to manage this such as using
          // tokens, DRM, and other formats however disabling the contextmenu
          // is the easiest solution to the problem. right clicking on a video
          // will not do anything when downloads are disabled.
          if (!canDownloadVideo) {
            e.preventDefault();
          }
        }}
        poster={posterImageUrl}
        preload="auto"
        data-setup={JSON.stringify({ 
          nativeControlsForTouch: this.getUseNativeControlsForTouch(),
          techOrder: this.getTechOrder(),
          plugins: this.getPlugins(),
          youtube: {
            enablePrivacyEnhancedMode: true,
            iv_load_policy: 3, // Dont show video annotations
            rel: 0, // Only show related videos from this channel
            // showinfo: 0, Deprecated by youtube 2018/9/25
            autoplay: autoplay ? 1 : 0 } 
          })}
          {...iframeWrapperProps}
          >
        {
          map(sources, (sourceProps, i)=> 
            <source key={i} {...sourceProps} />
          )
        }

        {
          subtitleTrack &&
          <track 
            kind="captions"
            src={subtitleTrack.url || subtitleTrack}
            srcLang={subtitleTrack.lang || 'en'}
            label={subtitleTrack.langLabel || 'English'}
          />
        }
      </video>

    );
  }
}


VideoishPlayer.propTypes = PROP_TYPES;
VideoishPlayer.defaultProps = PROP_DEFAULTS;

export { VideoishPlayer as VideoishPlayerComponent };
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(VideoishPlayer);